/**
 * Local intent detection — no cloud AI required for ERP write actions.
 */

import i18n from '../../i18n';
import { AGENT_ACTION_TYPES } from '../constants/actionTypes';
import { getAgentDocumentTypeLabel } from './agentDocumentHandoff';

const ip = (key, opts) => i18n.t(`modules.shared.aiAgent.localIntent.${key}`, opts);

function normalizeMessage(message) {
  return String(message || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]+/g, '');
}

function extractClientName(message) {
  const patterns = [
    /(?:pour\s+)?(?:un\s+)?client\s+([a-z0-9][a-z0-9\s\-_.]{0,60})/i,
    /client\s+([a-z0-9][a-z0-9\s\-_.]{0,60})/i,
    /pour\s+([a-z0-9][a-z0-9\s\-_.]{2,60})/i,
  ];

  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match?.[1]) {
      return match[1].trim();
    }
  }
  return null;
}

function extractAmount(message) {
  const match = message.match(/(\d[\d\s.,]*)\s*(?:da|din|dinar)?/i);
  if (!match) return 0;
  const raw = match[1].replace(/\s/g, '').replace(',', '.');
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function extractProductName(message) {
  const match = message.match(
    /(?:produit|article)\s+([a-z0-9][a-z0-9\s\-_.]{0,60})/i
  );
  return match?.[1]?.trim() || null;
}

function extractSupplierName(message) {
  const match = message.match(
    /(?:fournisseur|supplier)\s+([a-z0-9][a-z0-9\s\-_.]{0,60})/i
  );
  return match?.[1]?.trim() || null;
}

export function detectLocalAgentAction(message) {
  const text = normalizeMessage(message);
  const raw = String(message || '');

  const isCreate =
    /\b(cree|creee|creer|ajoute|ajouter|nouveau|nouvelle|genere|generer)\b/.test(text) ||
    /cr[eéè]e/i.test(raw);

  if (
    isCreate &&
    /\b(bon de commande|bon commande|purchase order|commande fournisseur)\b/.test(text)
  ) {
    return {
      type: AGENT_ACTION_TYPES.CREATE_INVOICE,
      params: {
        supplier: extractSupplierName(raw) || extractClientName(raw) || ip('fallback_supplier'),
        amount: extractAmount(raw),
        items: [],
        type: 'purchase-order',
      },
      requires_confirmation: true,
    };
  }

  if (isCreate && /\b(facture|invoice|devis|quote|proforma)\b/.test(text)) {
    const docType = text.includes('devis') || text.includes('quote')
      ? 'quote'
      : text.includes('proforma')
        ? 'proforma'
        : 'invoice';

    return {
      type: AGENT_ACTION_TYPES.CREATE_INVOICE,
      params: {
        client: extractClientName(raw) || ip('fallback_client'),
        amount: extractAmount(raw),
        items: [],
        type: docType,
      },
      requires_confirmation: true,
    };
  }

  if (isCreate && /\b(client|fournisseur|supplier)\b/.test(text)) {
    const name =
      extractClientName(raw) ||
      raw.match(/client\s+([^\n,]+)/i)?.[1]?.trim() ||
      ip('fallback_client');
    return {
      type: AGENT_ACTION_TYPES.CREATE_CLIENT,
      params: { name, type: ip('fallback_company_type') },
      requires_confirmation: true,
    };
  }

  if (isCreate && /\b(produit|article|product)\b/.test(text)) {
    return {
      type: AGENT_ACTION_TYPES.CREATE_PRODUCT,
      params: {
        name: extractProductName(raw) || ip('fallback_product'),
        price: extractAmount(raw),
        stock: 0,
      },
      requires_confirmation: true,
    };
  }

  return null;
}

export function buildLocalConfirmationMessage(action) {
  if (!action) return ip('confirm_default');

  switch (action.type) {
    case AGENT_ACTION_TYPES.CREATE_INVOICE: {
      const docType = action.params?.type || 'invoice';
      const docLabel = getAgentDocumentTypeLabel(docType);
      const partner =
        action.params?.client ||
        action.params?.clientName ||
        action.params?.supplier ||
        action.params?.supplierName ||
        '—';
      return ip('prepare_draft', {
        docLabel,
        partner,
        amount: action.params?.amount || 0,
      });
    }
    case AGENT_ACTION_TYPES.CREATE_CLIENT:
      return ip('create_client', { name: action.params.name });
    case AGENT_ACTION_TYPES.CREATE_PRODUCT:
      return ip('create_product', {
        name: action.params.name,
        price: action.params.price
          ? ip('create_product_price', { price: action.params.price })
          : '',
      });
    default:
      return ip('confirm_default');
  }
}
